<template>
  <div class="student-view">
    <el-card class="box-card" v-loading="loading">
      <template #header>
        <div class="card-header">
          <h2>{{ form.name ? form.name : '<New Student>' }}</h2>
          <div class="header-actions">
            <el-icon v-if="$route.params.id" @click="newStudent">
              <Plus />
            </el-icon>
          </div>
        </div>
      </template>

      <div class="student-details">
        <el-form :model="form" label-width="100px" :rules="rules" ref="formRef">
          <el-form-item label="Avatar" prop="avatar" v-if="$route.params.id">
            <el-upload class="avatar-uploader" :action="`${BACKEND_URL}/api/student/${$route.params.id}/avatar`"
              :show-file-list="false" :on-success="handleAvatarSuccess" :before-upload="beforeAvatarUpload"
              :on-preview="handleAvatarPreview" :disabled="!isEditing" :with-credentials="true">
              <img v-if="imageUrl" :src="imageUrl" class="avatar" @click="handleAvatarPreview" />
              <el-icon v-else class="avatar-uploader-icon">
                <Plus />
              </el-icon>
            </el-upload>
          </el-form-item>
          <el-form-item label="Name" prop="name">
            <el-input v-model="form.name" :disabled="!isEditing" />
          </el-form-item>
          <el-form-item label="ID No" prop="idNO">
            <el-input v-model="form.idNO" :disabled="!isEditing" />
          </el-form-item>
          <!-- <el-form-item label="Parents" :span="2">
            <div v-if="student && student.parents && student.parents.length > 0">
              <el-tag 
                v-for="parent in student.parents" 
                :key="parent.id"
                class="parent-tag"
                type="info"
              >
                {{ parent.name }}
              </el-tag>
            </div>
            <span v-else>No parents assigned</span>
          </el-form-item> -->
          <el-form-item>
            <el-button v-if="isEditing" type="success" @click="handleSubmit" :loading="submitting">
              {{ $route.params.id ? 'Update' : 'Create' }}
            </el-button>
            <el-button type="primary" @click="toggleEdit">
              {{ isEditing ? 'Cancel' : 'Edit Information' }}
            </el-button>
          </el-form-item>
        </el-form>
      </div>
    </el-card>
    <PhotoDialog v-model="avatarDialog" :src="imageUrl" />
  </div>
</template>

<script>
import axios from 'axios';
import { BACKEND_URL } from '../../../share-source/src/config/config.js';
import { ElMessage } from 'element-plus';
import { Plus } from '@element-plus/icons-vue';
import { useUserStore } from '../stores/Global';
import PhotoDialog from '../../../share-source/src/components/PhotoDialog.vue';

export default {
  name: 'StudentInfo',
  setup() {
    const userStore = useUserStore();
    return {
      userStore
    };
  },
  data() {
    return {
      loading: false,
      submitting: false,
      isEditing: false,
      formRef: null,
      form: {
        name: '',
        idNO: '',
        parents: [],
        avatar: ''
      },
      rules: {
        name: [
          { required: true, message: 'Name is required', trigger: 'blur' },
          { min: 2, max: 20, message: 'Length must be 2 to 20 characters', trigger: 'blur' }
        ],
        idNO: [
          { required: true, message: 'ID No is required', trigger: 'blur' },
          { min: 2, max: 20, message: 'Length must be 2 to 20 characters', trigger: 'blur' }
        ]
      },
      BACKEND_URL: BACKEND_URL,
      avatarDialog: false,
    }
  },
  computed: {
    imageUrl() {
      if (this.form.avatar) {
        return `${BACKEND_URL}${this.form.avatar}`;
      }
      return null;
    }
  },
  components: {
    Plus,
    PhotoDialog
  },
  mounted() {
    if (this.$route.params.id) {
      this.loadStudent();
    } else {
      this.form = {
        name: '',
        idNO: '',
        parents: [],
        avatar: ''
      };
    }
  },
  methods: {
    newStudent() {
      this.$router.push({ name: 'StudentInfo' });
      this.form = {
        name: '',
        idNO: '',
        parents: [],
        avatar: ''
      };
    },
    // eslint-disable-next-line no-unused-vars
    handleAvatarPreview(file) {
      if (!this.isEditing) {
        this.avatarDialog = true;
      }
    },
    async loadStudent() {
      this.loading = true;
      try {
        const response = await axios.get(`${BACKEND_URL}/api/student/${this.$route.params.id}`);
        this.form = {
          name: response.data.name,
          idNO: response.data.idNO,
          parents: response.data.parents || [],
          avatar: response.data.avatar || ''
        };
      } catch (error) {
        console.error('Error loading student:', error);
        ElMessage.error('Failed to load student information');
      } finally {
        this.loading = false;
      }
    },
    toggleEdit() {
      if (this.isEditing) {
        if (this.$route.params.id) {
          this.loadStudent();
        } else {
          this.form = {
            name: '',
            idNO: '',
            parents: [],
            avatar: ''
          };
        }
      }
      this.isEditing = !this.isEditing;
    },
    handleAvatarSuccess(response, uploadFile) {
      console.log('uploadFile', uploadFile);
      this.form.avatar = response.url;
    },
    beforeAvatarUpload(rawFile) {
      if (rawFile.type !== 'image/jpeg' && rawFile.type !== 'image/png') {
        ElMessage.error('Avatar picture must be JPG or PNG format!');
        return false;
      } else if (rawFile.size / 1024 / 1024 > 10) {
        ElMessage.error('Avatar picture size can not exceed 10MB!');
        return false;
      }
      return true;
    },
    async createStudent(data) {
      try {
        const response = await axios.post(`${BACKEND_URL}/api/student/parent/${this.userStore.getUserId()}`, data);
        ElMessage.success('Student created successfully!');
        return response;
      } catch (error) {
        ElMessage.error('Failed to create student');
        throw error;
      }
    },
    async updateStudent(id, data) {
      try {
        await axios.put(`${BACKEND_URL}/api/student/${id}`, data);
        ElMessage.success('Student updated successfully!');
        return true;
      } catch (error) {
        ElMessage.error('Failed to update student');
        throw error;
      }
    },
    async handleSubmit() {
      if (!this.$refs.formRef) return;
      
      try {
        const valid = await this.$refs.formRef.validate();
        if (!valid) return;

        this.submitting = true;
        const id = this.$route.params.id;
        const data = {
          name: this.form.name,
          idNO: this.form.idNO,
          avatar: this.form.avatar
        };

        if (id) {
          await this.updateStudent(id, data);
          this.isEditing = false;
          await this.loadStudent();
        } else {
          const response = await this.createStudent(data);
          this.$router.push({ name: 'StudentInfo', params: { id: response.data.id } });
        }
      } catch (error) {
        console.error('Form submission error:', error);
      } finally {
        this.submitting = false;
      }
    }
  }
}
</script>

<style scoped>
.student-view {
  display: flex;
  flex-direction: column;
}

.box-card {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.header-actions {
  display: flex;
  align-items: center;
  /* gap: 8px; */
  /* margin: 10px; */
}

.student-details {
  margin-top: 20px;
}

.parent-tag {
  margin-right: 8px;
  margin-bottom: 8px;
}

:deep(.el-card__body) {
  display: flex;
  flex-direction: column;
}

.avatar-uploader .avatar {
  width: 178px;
  height: 178px;
  display: block;
  object-fit: contain;
}

.avatar-uploader .el-upload {
  border: 1px dashed var(--el-border-color);
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  transition: var(--el-transition-duration-fast);
}

.avatar-uploader .el-upload:hover {
  border-color: var(--el-color-primary);
}

.el-icon.avatar-uploader-icon {
  font-size: 28px;
  color: #8c939d;
  width: 178px;
  height: 178px;
  text-align: center;
}
</style> 